import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Wrench, Cpu, MapPin, BadgeCheck, Truck, MessageCircle, CalendarCheck, FileText, Phone, Tag } from "lucide-react";
import { useLang } from "@/i18n";
import { waLink, GARAGE, submitRequest } from "@/config";
import { Field, Input, Textarea, Select, Gdpr, Honeypot, PhotoUpload, SuccessBox, SubmitBtn, useFormState, todayISO } from "@/components/FormBits";
import { toast } from "sonner";

const Section = ({ id, title, desc, icon: Icon, children, testid }) => (
  <section id={id} data-testid={testid} className="scroll-mt-24 container-x py-14 border-b border-zinc-800/60 last:border-0">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#E11D48]/10 border border-[#E11D48]/30 flex items-center justify-center shrink-0">
        <Icon size={22} className="text-[#E11D48]" />
      </div>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{title}</h2>
        {desc && <p className="mt-1 text-sm sm:text-base text-zinc-400 max-w-2xl">{desc}</p>}
      </div>
    </div>
    <div className="mt-8">{children}</div>
  </section>
);

const ItemGrid = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
    {items.map((it, i) => (
      <div key={i} className="flex items-center gap-2 card-dark px-4 py-3 text-sm text-zinc-200">
        <Check size={14} className="text-[#E11D48] shrink-0" />{it}
      </div>
    ))}
  </div>
);

function MobileDiagnosisForm() {
  const { t } = useLang();
  const [data, set] = useFormState({ name: "", phone: "", email: "", address: "", brand: "", model: "", year: "", plate: "", vin: "", problem: "", date: "", time: "", website: "", gdpr: false });
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitRequest("mobile_diagnosis", data, files);
      setDone(true);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  if (done) return <SuccessBox message={t("svc.mobiel.success")} testid="mobile-success" />;

  return (
    <form onSubmit={submit} data-testid="mobile-diagnosis-form" className="card-dark p-6 grid sm:grid-cols-2 gap-4">
      <Honeypot value={data.website} onChange={set("website")} />
      <Field label={t("form.name")} required><Input required value={data.name} onChange={set("name")} data-testid="md-name" /></Field>
      <Field label={t("form.phone")} required><Input required type="tel" value={data.phone} onChange={set("phone")} data-testid="md-phone" /></Field>
      <Field label={t("form.email")} required><Input required type="email" value={data.email} onChange={set("email")} data-testid="md-email" /></Field>
      <Field label={t("form.address")} required><Input required value={data.address} onChange={set("address")} data-testid="md-address" /></Field>
      <Field label={t("form.brand")} required><Input required value={data.brand} onChange={set("brand")} data-testid="md-brand" /></Field>
      <Field label={t("form.model")} required><Input required value={data.model} onChange={set("model")} data-testid="md-model" /></Field>
      <Field label={t("form.year")} required><Input required type="number" min="1980" max="2030" value={data.year} onChange={set("year")} data-testid="md-year" /></Field>
      <Field label={t("form.plate")} required><Input required value={data.plate} onChange={set("plate")} data-testid="md-plate" /></Field>
      <Field label={t("form.vin")}><Input value={data.vin} onChange={set("vin")} data-testid="md-vin" /></Field>
      <Field label={t("form.date")} required><Input required type="date" min={todayISO()} value={data.date} onChange={set("date")} data-testid="md-date" /></Field>
      <Field label={t("form.time")} required><Input required type="time" value={data.time} onChange={set("time")} data-testid="md-time" /></Field>
      <div className="sm:col-span-2"><Field label={t("form.problem")} required><Textarea required value={data.problem} onChange={set("problem")} data-testid="md-problem" /></Field></div>
      <div className="sm:col-span-2"><Field label={t("form.photos")}><PhotoUpload files={files} setFiles={setFiles} testid="md-photos" /></Field></div>
      <div className="sm:col-span-2"><Gdpr checked={data.gdpr} onChange={(v) => set("gdpr")({ target: { value: v } })} /></div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
        <SubmitBtn loading={loading} label={t("appt.send")} testid="md-submit" />
        <a href={waLink(t("wa.diagnose"))} target="_blank" rel="noreferrer" className="btn-wa" data-testid="md-whatsapp">
          <MessageCircle size={18} />{t("svc.mobiel.wa")}
        </a>
      </div>
      <p className="sm:col-span-2 text-xs text-zinc-500 text-center" data-testid="md-deposit-note">{t("form.depositNote")}</p>
    </form>
  );
}

function KeuringForm() {
  const { t } = useLang();
  const [data, set] = useFormState({ name: "", phone: "", plate: "", brand: "", model: "", year: "", service: "", date: "", remarks: "", website: "", gdpr: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const services = t("svc.keuring.services");

  useEffect(() => {
    if (!data.service) set("service")({ target: { value: t("svc.keuring.services")[0] } });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitRequest("keuring", data); setDone(true); }
    catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  if (done) return <SuccessBox message={t("svc.keuring.success")} testid="keuring-success" />;

  return (
    <form onSubmit={submit} data-testid="keuring-form" className="card-dark p-6 grid sm:grid-cols-2 gap-4">
      <Honeypot value={data.website} onChange={set("website")} />
      <Field label={t("form.name")} required><Input required value={data.name} onChange={set("name")} data-testid="k-name" /></Field>
      <Field label={t("form.phone")} required><Input required type="tel" value={data.phone} onChange={set("phone")} data-testid="k-phone" /></Field>
      <Field label={t("form.plate")} required><Input required value={data.plate} onChange={set("plate")} data-testid="k-plate" /></Field>
      <Field label={t("form.brand")} required><Input required value={data.brand} onChange={set("brand")} data-testid="k-brand" /></Field>
      <Field label={t("form.model")} required><Input required value={data.model} onChange={set("model")} data-testid="k-model" /></Field>
      <Field label={t("form.year")} required><Input required type="number" min="1980" max="2030" value={data.year} onChange={set("year")} data-testid="k-year" /></Field>
      <Field label={t("svc.keuring.serviceLabel")} required><Select options={services} value={data.service} onChange={set("service")} data-testid="k-service" /></Field>
      <Field label={t("form.date")} required><Input required type="date" min={todayISO()} value={data.date} onChange={set("date")} data-testid="k-date" /></Field>
      <div className="sm:col-span-2"><Field label={t("form.remarks")}><Textarea value={data.remarks} onChange={set("remarks")} data-testid="k-remarks" /></Field></div>
      <div className="sm:col-span-2"><Gdpr checked={data.gdpr} onChange={(v) => set("gdpr")({ target: { value: v } })} /></div>
      <div className="sm:col-span-2"><SubmitBtn loading={loading} label={t("svc.keuring.cta")} testid="k-submit" /></div>
      <p className="sm:col-span-2 text-xs text-zinc-500 text-center" data-testid="k-deposit-note">{t("form.depositNote")}</p>
    </form>
  );
}

function TowForm() {
  const { t } = useLang();
  const [data, set] = useFormState({ name: "", phone: "", location: "", destination: "", vehicle: "", plate: "", reason: "", drives: "ja", website: "", gdpr: false });
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitRequest("tow", data, files); setDone(true); }
    catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  if (done) return <SuccessBox message={t("svc.sleep.success")} testid="tow-success" />;

  return (
    <form onSubmit={submit} data-testid="tow-form" className="card-dark p-6 grid sm:grid-cols-2 gap-4 border-[#E11D48]/30">
      <Honeypot value={data.website} onChange={set("website")} />
      <Field label={t("form.name")} required><Input required value={data.name} onChange={set("name")} data-testid="tow-name" /></Field>
      <Field label={t("form.phone")} required><Input required type="tel" value={data.phone} onChange={set("phone")} data-testid="tow-phone" /></Field>
      <Field label={t("svc.sleep.loc")} required><Input required value={data.location} onChange={set("location")} data-testid="tow-location" /></Field>
      <Field label={t("svc.sleep.dest")}><Input value={data.destination} onChange={set("destination")} data-testid="tow-destination" /></Field>
      <Field label={`${t("form.brand")} & ${t("form.model")}`} required><Input required value={data.vehicle} onChange={set("vehicle")} data-testid="tow-vehicle" /></Field>
      <Field label={t("form.plate")} required><Input required value={data.plate} onChange={set("plate")} data-testid="tow-plate" /></Field>
      <div className="sm:col-span-2">
        <label className="label-dark">{t("svc.sleep.drives")}</label>
        <div className="flex gap-3">
          {[["ja", t("svc.sleep.yes")], ["nee", t("svc.sleep.no")]].map(([v, label]) => (
            <button type="button" key={v} onClick={() => set("drives")({ target: { value: v } })} data-testid={`tow-drives-${v}`}
              className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${data.drives === v ? "border-[#E11D48] bg-[#E11D48]/10 text-white" : "border-zinc-700 text-zinc-400"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2"><Field label={t("svc.sleep.reason")} required><Textarea required value={data.reason} onChange={set("reason")} data-testid="tow-reason" /></Field></div>
      <div className="sm:col-span-2"><Field label={t("svc.sleep.photo")}><PhotoUpload files={files} setFiles={setFiles} testid="tow-photos" /></Field></div>
      <div className="sm:col-span-2"><Gdpr checked={data.gdpr} onChange={(v) => set("gdpr")({ target: { value: v } })} /></div>
      <div className="sm:col-span-2 flex flex-col gap-3">
        <SubmitBtn loading={loading} label={t("svc.sleep.big")} testid="tow-submit" />
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={waLink(t("wa.tow"))} target="_blank" rel="noreferrer" className="btn-wa flex-1" data-testid="tow-whatsapp">
            <MessageCircle size={18} />{t("svc.sleep.waLoc")}
          </a>
          <a href={GARAGE.phoneHref} className="btn-outline flex-1" data-testid="tow-call">
            <Phone size={18} />{t("svc.sleep.call")}
          </a>
        </div>
      </div>
      <p className="sm:col-span-2 text-xs text-zinc-500 text-center" data-testid="tow-deposit-note">{t("form.depositNote")}</p>
    </form>
  );
}

function PricingSection() {
  const { t } = useLang();
  const p = t("pricing");
  return (
    <section id="tarieven" data-testid="pricing-section" className="scroll-mt-24 container-x py-14 border-b border-zinc-800/60">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#E11D48]/10 border border-[#E11D48]/30 flex items-center justify-center shrink-0">
          <Tag size={22} className="text-[#E11D48]" />
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{p.title}</h2>
          <p className="mt-1 text-sm sm:text-base text-zinc-400 max-w-2xl">{p.sub}</p>
        </div>
      </div>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {p.rows.map(([label, price], i) => (
          <div key={i} data-testid={`price-row-${i}`} className="card-dark px-5 py-4 flex items-center justify-between gap-4 hover:border-[#E11D48]/40 transition-colors">
            <span className="text-sm text-zinc-300">{label}</span>
            <span className="font-mono2 font-bold text-white whitespace-nowrap">{price}</span>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-zinc-500 italic max-w-2xl" data-testid="pricing-disclaimer">{p.disclaimer}</p>
      <Link to="/offerte" className="btn-red mt-6" data-testid="pricing-cta"><FileText size={18} />{p.cta}</Link>
    </section>
  );
}

export default function Diensten() {
  const { t } = useLang();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div data-testid="diensten-page">
      <div className="container-x pt-14 pb-2">
        <p className="overline-red">Milano Worx</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">{t("nav.diensten")}</h1>
      </div>

      <Section id="onderhoud" testid="svc-onderhoud" icon={Wrench} title={t("svc.onderhoud.title")} desc={t("svc.onderhoud.desc")}>
        <ItemGrid items={t("svc.onderhoud.items")} />
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link to="/offerte" className="btn-red" data-testid="onderhoud-quote-cta"><FileText size={18} />{t("quote.title")}</Link>
          <Link to="/afspraak" className="btn-outline" data-testid="onderhoud-appointment-cta"><CalendarCheck size={18} />{t("appt.title")}</Link>
        </div>
      </Section>

      <Section id="diagnose" testid="svc-diagnose" icon={Cpu} title={t("svc.diagnose.title")} desc={t("svc.diagnose.desc")}>
        <ItemGrid items={t("svc.diagnose.items")} />
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link to="/afspraak" className="btn-red" data-testid="diagnose-appointment-cta"><CalendarCheck size={18} />{t("svc.diagnose.cta")}</Link>
          <a href={waLink(t("wa.diagnose"))} target="_blank" rel="noreferrer" className="btn-wa" data-testid="diagnose-whatsapp-cta"><MessageCircle size={18} />WhatsApp</a>
        </div>
      </Section>

      <PricingSection />

      <Section id="mobiel" testid="svc-mobiel" icon={MapPin} title={t("svc.mobiel.title")} desc={t("svc.mobiel.desc")}>
        <MobileDiagnosisForm />
      </Section>

      <Section id="keuring" testid="svc-keuring" icon={BadgeCheck} title={t("svc.keuring.title")}>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {[["z", "zd"], ["verk", "verkd"], ["per", "perd"]].map(([tt, dd], i) => (
            <div key={i} className="card-dark p-5">
              <h3 className="font-semibold text-zinc-100">{t(`svc.keuring.${tt}`)}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{t(`svc.keuring.${dd}`)}</p>
            </div>
          ))}
        </div>
        <p className="mb-6 text-xs text-zinc-500 italic max-w-2xl" data-testid="keuring-disclaimer">{t("svc.keuring.disclaimer")}</p>
        <KeuringForm />
      </Section>

      <Section id="sleep" testid="svc-sleep" icon={Truck} title={t("svc.sleep.title")} desc={t("svc.sleep.sub")}>
        <a href={waLink(t("wa.tow"))} target="_blank" rel="noreferrer" data-testid="tow-big-cta"
          className="btn-red w-full sm:w-auto !text-lg !px-10 !py-5 mb-8">
          <Truck size={22} />{t("svc.sleep.big")}
        </a>
        <TowForm />
      </Section>
    </div>
  );
}
